import clsx from 'clsx';
import { Suspense, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';
import { OddsLogicCredit } from '@/components/shared/OddsLogicCredit';

import { formatRecord } from './BreakdownTabs';
import type { ResultsFilters } from './FilterTypes';
import { ResultsChart, ResultsChartLoading } from './ResultsChart';
import { SeasonPicker } from './SeasonPicker';
import { RESULTS_CONTROL, RESULTS_CONTROL_IDLE, SPORT_IDS, filterUrl } from './consts';
import { useResultsSummary } from './useResultsSummary';

// Sport chips, in volume order (CBB is the largest by graded volume). The tail sports
// (NBA/WNBA/CFL/college baseball) live behind "All sports" (#2004).
const CHIPS: { key: string; label: string; url: string }[] = [
  { key: 'cbb', label: 'CBB', url: 'cbb' },
  { key: 'cfb', label: 'CFB', url: 'cfb' },
  { key: 'nfl', label: 'NFL', url: 'nfl' },
  { key: 'all', label: 'All sports', url: 'all' },
];

const SPORT_FULL: Record<string, string> = {
  '1': 'College football',
  '2': 'College basketball',
  '3': 'NFL',
  '0': 'All sports',
  cfb: 'College football',
  cbb: 'College basketball',
  nfl: 'NFL',
  all: 'All sports',
  ras: 'RAS sports',
  'nfl,cfb': 'NFL & CFB',
  'cfb,nfl': 'NFL & CFB',
};

function sportKeyForChip(sportId?: string): string {
  if (!sportId || sportId === 'ras') {
    return '';
  }
  if (sportId === '0') {
    return 'all';
  }
  // numeric → name
  const byId = Object.entries(SPORT_IDS).find(([, id]) => `${id}` === `${sportId}`)?.[0];
  return byId || sportId;
}

function seasonPhrase(
  seasonId: string | undefined,
  sportId: string | undefined,
  seasons?: API.Season[],
): string {
  if (seasonId && /^\d+$/.test(seasonId)) {
    return seasons?.find((s) => `${s.id}` === `${seasonId}`)?.name || 'season';
  }
  switch (seasonId) {
    case 'all-time':
      return 'all-time';
    case 'last':
      return 'last season';
    case 'last-2':
      return 'last 2 seasons';
    case 'last-3':
      return 'last 3 seasons';
    case 'last-5':
      return 'last 5 seasons';
    default: {
      // current: the most recent season's name for a single sport, else "this season".
      const key = sportKeyForChip(sportId);
      const num = (SPORT_IDS as Record<string, number>)[key];
      if (num && seasons) {
        const latest = seasons
          .filter((s) => s.sportId === num && !s.hidden && s.endDate)
          .sort((a, b) => Date.parse(b.endDate || '') - Date.parse(a.endDate || ''))[0];
        if (latest?.name) {
          return latest.name;
        }
      }
      return 'this season';
    }
  }
}

/**
 * The results header card (#2004): the season title + sub on the left and the OddsLogic
 * credit top-right, then a nav row — the My/All scope toggle, the sport chips, and the
 * custom season picker. The stats + units chart render below it (ResultsBreakdowns).
 */
export function ResultsHeader({
  baseUrl,
  userId,
  filters,
  sportId,
  seasonId,
  seasons,
  scope,
  onScopeChange,
  canToggle,
  hasNoPlays = false,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  sportId?: string;
  seasonId?: string;
  seasons?: API.Season[];
  // 'me' → My results, otherwise All results (the public archive feed).
  scope: 'me' | 'archive';
  onScopeChange: (scope: 'me' | 'archive') => void;
  canToggle: boolean;
  // Nothing graded and nothing pending in this view: skip the chart, which would plot
  // an empty series with an invented axis (#2004).
  hasNoPlays?: boolean;
}) {
  const summary = useResultsSummary(userId, filters);
  const { wins, losses, pushes, units, isLoading } = summary;
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : '—';
  const chipKey = sportKeyForChip(sportId);
  const seasonIdIsNumber = !!seasonId && /^\d+$/.test(seasonId);

  const title = useMemo(() => {
    const sport = SPORT_FULL[sportId || 'ras'] || 'Results';
    return `${sport} · ${seasonPhrase(seasonId, sportId, seasons)}`;
  }, [sportId, seasonId, seasons]);

  const sub = scope === 'me' ? 'Only the plays on your account' : 'Every play RAS has released';

  return (
    <div className="mb-3 rounded-2xl border border-zinc-950/[0.08] bg-white p-4 shadow-xs sm:p-5 dark:border-white/10 dark:bg-zinc-900">
      {/* Title takes the full card width — the credit lives at the bottom-right (below
          the chart) so it never squeezes the title into a wrap (#2004). */}
      <div className="mb-4">
        <h1 className="font-bold text-xl text-zinc-900 tracking-tight sm:text-2xl dark:text-white">
          {title}
        </h1>
        <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">{sub}</p>
      </div>

      {/* Two rows: scope + the view you're in on top, the sport chips beneath. All share
          RESULTS_CONTROL sizing. Keeping the chips as their own row stops them breaking
          mid-set (three up, "All sports" orphaned below) as the widths change. */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {canToggle && (
            <div className="inline-flex h-8 shrink-0 items-center rounded-lg border border-zinc-950/15 bg-zinc-50 p-0.5 dark:border-white/15 dark:bg-white/5">
              {(['me', 'archive'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onScopeChange(s)}
                  aria-pressed={scope === s}
                  className={twMerge(
                    'inline-flex h-full items-center rounded-md px-3 font-semibold text-xs',
                    scope === s
                      ? 'bg-emerald-700 text-white'
                      : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white',
                  )}
                >
                  {s === 'me' ? 'My results' : 'All results'}
                </button>
              ))}
            </div>
          )}

          {/* The picker names the view you're in ("All sports · all-time"); the chips
              below narrow it, so it reads scope-then-refinement (#2004). */}
          <SeasonPicker
            baseUrl={baseUrl}
            sportId={sportId}
            seasonId={seasonId}
            seasons={seasons}
            label={title}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {CHIPS.map((chip) => {
            const active = chipKey === chip.key;
            return (
              <a
                key={chip.key}
                href={filterUrl(baseUrl, chip.url, seasonIdIsNumber ? undefined : seasonId, '')}
                aria-pressed={active}
                className={twMerge(
                  RESULTS_CONTROL,
                  active ? 'border-emerald-700 bg-emerald-700 text-white' : RESULTS_CONTROL_IDLE,
                )}
              >
                {chip.label}
              </a>
            );
          })}
        </div>
      </div>

      {/* Record / win rate / units, then the cumulative units chart — all in the one
          header card (#2004). A natural flex row (not forced equal columns) so the record
          never gets squeezed into a wrap/clip; stats wrap as whole units if too narrow. */}
      {/* One line, never re-wrapped. Wrapping here was data-dependent — a wide record
          ("4892-3011-6") pushed Units onto a second line while a narrow one ("0-0") did
          not, so the row grew and shrank as you moved between sports and everything
          below the card jumped with it. The type scales to fit instead (#2004). */}
      <div className="mt-5 flex flex-nowrap items-start gap-x-6 sm:gap-x-10">
        <Stat label="Record">
          <Skeleton loading={isLoading} className="inline-block">
            {isLoading ? '000-000' : formatRecord({ w: wins, l: losses, p: pushes, u: 0 })}
          </Skeleton>
        </Stat>
        <Stat label="Win rate">
          <Skeleton loading={isLoading} className="inline-block">
            {winRate}%
          </Skeleton>
        </Stat>
        <Stat label="Units">
          <Skeleton loading={isLoading} as="span" className="inline-block">
            <span className={clsx(units >= 0 ? 'text-emerald-500' : 'text-rose-500')}>
              {units >= 0 ? '+' : ''}
              {units.toFixed(2)}
            </span>
          </Skeleton>
        </Stat>
      </div>
      {!hasNoPlays && (
        <Suspense fallback={<ResultsChartLoading />}>
          <ResultsChart userId={userId} filters={filters} baseUrl={baseUrl} />
        </Suspense>
      )}

      {/* Credit lives at the bottom-right of the card at every width, so it never
          competes with the title/chips for horizontal room. */}
      <CreditBlock className="mt-3 flex" />
    </div>
  );
}

/** "Odds by OddsLogic" credit + the freshness note, right-aligned. */
function CreditBlock({ className }: { className?: string }) {
  return (
    <div className={twMerge('flex-col items-end gap-1', className)}>
      <OddsLogicCredit />
      <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
        Live lines may be delayed a few minutes
      </span>
    </div>
  );
}

/** One header stat tile: an uppercase label over a large tabular value. */
function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="shrink-0">
      <p className="mb-1 font-semibold text-[10px] text-zinc-500 uppercase tracking-wider dark:text-zinc-400">
        {label}
      </p>
      {/* Never wrap: the record ("862-648-18") would otherwise break at its hyphens. The
          stat sizes to its content (shrink-0 in a flex row), so it never clips either. */}
      {/* Fluid so the three stats always fit one line: at the narrowest card width the
          widest real record ("4892-3011-6") still clears, so the row never re-wraps and
          never clips. */}
      <p className="whitespace-nowrap font-bold text-[clamp(1rem,3.4vw,1.5rem)] text-zinc-900 tabular-nums tracking-tight dark:text-white">
        {children}
      </p>
    </div>
  );
}
